import { z } from 'zod';

const nullableText = z.preprocess(
  value => (value === '' || value == null ? null : String(value)),
  z.string().nullable(),
);

const nullableNumber = z.preprocess(value => {
  if (value === '' || value == null) return null;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : value;
}, z.number().nullable());

const nullableInteger = z.preprocess(value => {
  if (value === '' || value == null) return null;
  const parsed = Number(value);
  return Number.isInteger(parsed) ? parsed : value;
}, z.number().int().nullable());

const requiredInteger = z.preprocess(value => {
  const parsed = Number(value);
  return Number.isInteger(parsed) ? parsed : value;
}, z.number().int());

const clickHouseBoolean = z.preprocess(value => {
  if (value === true || value === 1 || value === '1') return true;
  if (value === false || value === 0 || value === '0') return false;
  return value;
}, z.boolean());

const clickHouseLocationBoolean = z.preprocess(
  value => (value == null ? false : value),
  clickHouseBoolean,
);

const postgresBoolean = z.preprocess(value => {
  if (value === true || value === 'true' || value === 1 || value === '1') return true;
  if (value === false || value === 'false' || value === 0 || value === '0') return false;
  return value;
}, z.boolean());

export const RiderDayEvidenceKindSchema = z.enum([
  'LOCATION',
  'ATTENDANCE',
  'ARRIVAL',
  'WORK_ACTION',
  'CUSTOMER_CONTACT',
  'SCAN',
  'PAYMENT',
  'TRACKING',
  'ODOMETER',
  'OTHER',
]);

export const RiderDayEvidenceRowSchema = z
  .object({
    event_id: z.string().min(1),
    event_name: z.string().min(1),
    evidence_kind: RiderDayEvidenceKindSchema,
    captured_at: z.string().min(1),
    synced_at: z.string().min(1),
    upload_delay_seconds: requiredInteger,
    latitude: nullableNumber,
    longitude: nullableNumber,
    accuracy_meters: nullableNumber,
    shift_id: nullableInteger,
    facility_id: nullableInteger,
    facility_name: nullableText,
    work_type: z.enum(['PACKAGE', 'RETURN']).nullable(),
    work_id: nullableInteger,
    action_type: nullableText,
    action_stage: nullableText,
    lifecycle_status: nullableText,
    app_version: nullableText,
    sequence_no: nullableInteger,
    tracking_mode: nullableText,
    is_mock_location: clickHouseBoolean,
    network_connected: clickHouseBoolean,
    battery_pct: nullableInteger,
    has_location: clickHouseLocationBoolean,
    total_rows: requiredInteger,
  })
  .strict();

export const RiderDayEvidenceRowsSchema = z.array(RiderDayEvidenceRowSchema);

export const RiderDayShiftRowSchema = z
  .object({
    shift_id: requiredInteger,
    rider_id: requiredInteger,
    facility_id: nullableInteger,
    facility_code: nullableText,
    facility_name: nullableText,
    shift_status: z.string().min(1),
    shift_started_at: z.string().min(1),
    shift_ended_at: nullableText,
    distance_window_ended_at: z.string().min(1),
    start_odometer_reading_km: nullableInteger,
    end_odometer_reading_km: nullableInteger,
    start_odometer_captured_at: nullableText,
    end_odometer_captured_at: nullableText,
    has_start_odometer_photo: postgresBoolean,
    has_end_odometer_photo: postgresBoolean,
    has_start_location: postgresBoolean,
    has_end_location: postgresBoolean,
    was_auto_closed: postgresBoolean,
    was_auto_closed_with_open_work: postgresBoolean,
    auto_close_reason: nullableText,
    auto_close_effective_ended_at: nullableText,
    source_updated_at: z.string().min(1),
    has_overlapping_shift: postgresBoolean,
  })
  .strict();

export const RiderDayShiftRowsSchema = z.array(RiderDayShiftRowSchema);

export const RiderDayDistanceRowSchema = z
  .object({
    rider_id: requiredInteger,
    business_date: z.string().min(1),
    shift_id: requiredInteger,
    algorithm_version: z.string().min(1),
    operational_observed_meters: nullableNumber,
    operational_supported_gap_meters: nullableNumber,
    suggested_operational_meters: nullableNumber,
    on_duty_observed_meters: nullableNumber,
    on_duty_supported_gap_meters: nullableNumber,
    suggested_on_duty_meters: nullableNumber,
    movement_outside_work_window_meters: nullableNumber,
    total_location_points: requiredInteger,
    accepted_location_points: requiredInteger,
    invalid_coordinate_points: requiredInteger,
    low_accuracy_points: requiredInteger,
    mock_location_points: requiredInteger,
    rejected_movement_segment_count: requiredInteger,
    supported_gap_count: requiredInteger,
    unresolved_gap_count: requiredInteger,
    unresolved_gap_seconds: requiredInteger,
    missing_sequence_gap_count: requiredInteger,
    supported_movement_seconds: requiredInteger,
    supported_evidence_seconds: requiredInteger,
    evidence_span_seconds: requiredInteger,
    evidence_coverage_percent: nullableNumber,
    work_interval_count: requiredInteger,
    qualifying_work_count: requiredInteger,
    incomplete_work_interval_count: requiredInteger,
    work_window_started_at: nullableText,
    work_window_ended_at: nullableText,
    first_accepted_at: nullableText,
    last_accepted_at: nullableText,
    source_watermark: nullableText,
    evidence_status: z.enum([
      'GOOD_COVERAGE',
      'PARTIAL_COVERAGE',
      'WORK_OUTCOME_INCOMPLETE',
      'INSUFFICIENT_EVIDENCE',
    ]),
  })
  .strict();

export const RiderDayDistanceRowsSchema = z.array(RiderDayDistanceRowSchema);

export type RiderDayEvidenceKind = z.infer<typeof RiderDayEvidenceKindSchema>;
export type RiderDayEvidenceRow = z.infer<typeof RiderDayEvidenceRowSchema>;
export type RiderDayShiftRow = z.infer<typeof RiderDayShiftRowSchema>;
export type RiderDayDistanceRow = z.infer<typeof RiderDayDistanceRowSchema>;

export type RiderDayQuery = {
  riderId: number;
  businessDate: string;
};

export type RiderDayDistanceQuery = RiderDayQuery & {
  shifts: Array<
    Pick<RiderDayShiftRow, 'shift_id' | 'shift_started_at' | 'distance_window_ended_at'>
  >;
};

export type RiderDayDistanceBatch = {
  rows: Array<RiderDayDistanceRow>;
  failedShiftIds: Array<number>;
};

/* Rider trip distance (rider-trip-distance-v6). One rider, one IST business
   date. Payable distance is movement while dispatched work is in hand, plus a
   return leg only when the rider re-enters a facility. Trips and legs explain
   that total; they never change it. */

const clickHouseFlag = z.preprocess(value => {
  if (value === true || value === 1 || value === '1') return true;
  if (value === false || value === 0 || value === '0' || value == null) return false;
  return value;
}, z.boolean());

const requiredNumber = z.preprocess(value => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : value;
}, z.number());

export const RiderDayTripSummaryRowSchema = z
  .object({
    rider_id: requiredInteger,
    business_date: z.string().min(1),
    rider_name: nullableText,
    agency: nullableText,
    operating_facilities: nullableText,
    shift_count: requiredInteger,
    /* Today is rebuilt every 2 hours; it is priced (payout columns) only the
       next morning. Optional so the card can be re-synced after this ships. */
    day_in_progress: clickHouseFlag.optional().default(false),
    payout_distance_km: nullableNumber,
    payout_distance_algorithm: nullableText,
    settlement_status: z.enum(['SETTLED_ON_V5', 'NOT_SETTLED']).nullable(),
    payout_evidence_status: nullableText,
    trip_payable_km: nullableNumber,
    trip_evidence_status: nullableText,
    trip_evidence_summary: nullableText,
    location_evidence_quality: nullableText,
    trip_count: nullableInteger,
    qualifying_work_count: nullableInteger,
    failed_delivery_attempt_count: nullableInteger,
    payable_measured_km: nullableNumber,
    payable_estimated_km: nullableNumber,
    estimated_share_percent: nullableNumber,
    unknown_km: nullableNumber,
    return_to_base_km: nullableNumber,
    movement_while_on_duty_km: nullableNumber,
    excluded_after_last_work_km: nullableNumber,
    excluded_no_work_km: nullableNumber,
    excluded_at_facility_km: nullableNumber,
    excluded_parked_km: nullableNumber,
    excluded_rejected_gps_km: nullableNumber,
    inferred_departure_count: nullableInteger,
    inferred_visit_count: nullableInteger,
    address_mismatch_count: nullableInteger,
    open_unresolved_count: nullableInteger,
    unverified_failed_attempt_count: nullableInteger,
    work_without_stop_km: nullableNumber,
    capped_return_count: nullableInteger,
    backend_only_start_count: nullableInteger,
    no_gps_estimated_km: nullableNumber,
    /* Added with the store<->bike allowance and late mark-out estimate. Optional
       so the page works before and after the card is re-synced. */
    facility_access_km: nullableNumber.optional().default(null),
    late_mark_out_approach_km: nullableNumber.optional().default(null),
    late_mark_out_count: nullableInteger.optional().default(null),
    rejected_gps_fix_count: nullableInteger.optional().default(null),
    /* Added with the ride-back credit, late completions and the freshness
       marker (2026-09-30). Optional so the page works before and after the
       card is re-synced. */
    ride_back_credit_km: nullableNumber.optional().default(null),
    ride_back_gps_km: nullableNumber.optional().default(null),
    return_floor_km: nullableNumber.optional().default(null),
    late_completion_count: nullableInteger.optional().default(null),
    computed_at: nullableText.optional().default(null),
    gps_received_until: nullableText.optional().default(null),
    ride_back_status: nullableText.optional().default(null),
    ride_back_store: nullableText.optional().default(null),
    ride_back_stop_to_store_km: nullableNumber.optional().default(null),
    ride_back_progress_km: nullableNumber.optional().default(null),
    shift_end_kind: nullableText.optional().default(null),
    shift_ended_at: nullableText.optional().default(null),
    /* Sign-in place, carried-over jobs and morning custody drops (2026-10-01). */
    custody_drop_credit_km: nullableNumber.optional().default(null),
    custody_drop_gps_km: nullableNumber.optional().default(null),
    custody_drop_store: nullableText.optional().default(null),
    carried_over_count: nullableInteger.optional().default(null),
    carried_over_unproven_count: nullableInteger.optional().default(null),
    sign_in_place: nullableText.optional().default(null),
    sign_in_qr_store: nullableText.optional().default(null),
    sign_in_from_qr_store_km: nullableNumber.optional().default(null),
    /* Trip continuity and leg caps (2026-10-06). */
    between_stops_km: nullableNumber.optional().default(null),
    detour_not_paid_km: nullableNumber.optional().default(null),
    detour_capped_leg_count: nullableInteger.optional().default(null),
    odometer_km: nullableNumber,
    odometer_status: nullableText,
    suggestion_status: nullableText,
    amount_basis: nullableText,
    suggested_amount: nullableNumber,
    amount_above_floor: nullableNumber,
    daily_minimum_guarantee: nullableNumber,
    included_daily_km: nullableNumber,
    extra_km_rate: nullableNumber,
    policy_source: nullableText,
    payout_authority_status: z.string().min(1),
  })
  .strict();

export const RiderDayTripSummaryRowsSchema = z.array(RiderDayTripSummaryRowSchema);

export const RiderDayTripStatusSchema = z.enum([
  'MEASURED',
  'INCLUDES_ESTIMATE',
  'HAS_UNKNOWN_DISTANCE',
]);

export const RiderDayTripRowSchema = z
  .object({
    rider_shift_id: requiredInteger,
    trip_number: requiredInteger,
    started_at: z.string().min(1),
    ended_at: z.string().min(1),
    started_from: nullableText,
    ended_at_label: nullableText,
    returned_to_facility: clickHouseFlag,
    leg_count: requiredInteger,
    stop_count: requiredInteger,
    delivered_count: requiredInteger,
    undelivered_count: requiredInteger,
    picked_up_count: requiredInteger,
    pickup_failed_count: requiredInteger,
    payable_km: requiredNumber,
    access_km: nullableNumber.optional().default(null),
    measured_km: requiredNumber,
    estimated_km: requiredNumber,
    unknown_km: requiredNumber,
    return_leg_km: requiredNumber,
    has_capped_return: clickHouseFlag,
    has_inferred_departure: clickHouseFlag,
    inferred_visit_count: requiredInteger,
    address_mismatch_count: requiredInteger,
    trip_status: RiderDayTripStatusSchema,
  })
  .strict();

export const RiderDayTripRowsSchema = z.array(RiderDayTripRowSchema);

export const RiderDayTripLegKindSchema = z.enum([
  'FACILITY_TO_STOP',
  'START_TO_STOP',
  'STOP_TO_STOP',
  'RETURN_TO_FACILITY',
  'WORK_WITHOUT_STOP',
]);

export const RiderDayTripLegRowSchema = z
  .object({
    rider_shift_id: requiredInteger,
    trip_number: requiredInteger,
    leg_number: requiredInteger,
    leg_kind: RiderDayTripLegKindSchema,
    from_label: nullableText,
    to_label: nullableText,
    to_stop_number: nullableInteger,
    items: nullableText,
    delivered_count: requiredInteger,
    undelivered_count: requiredInteger,
    picked_up_count: requiredInteger,
    pickup_failed_count: requiredInteger,
    started_at: z.string().min(1),
    ended_at: z.string().min(1),
    payable_km: requiredNumber,
    measured_km: requiredNumber,
    estimated_km: requiredNumber,
    unknown_km: requiredNumber,
    return_cap_km: nullableNumber,
    is_return_capped: clickHouseFlag,
    /* Every leg capped at 1.5x the normal road distance (2026-10-06). */
    detour_cap_km: nullableNumber.optional().default(null),
    is_detour_capped: clickHouseFlag.optional().default(false),
    capped_excess_km: nullableNumber.optional().default(null),
    has_inferred_departure: clickHouseFlag,
    has_inferred_visit: clickHouseFlag,
    has_address_mismatch: clickHouseFlag,
    estimated_gap_count: requiredInteger,
    unknown_gap_count: requiredInteger,
    straight_line_gap_count: requiredInteger,
    from_latitude: nullableNumber,
    from_longitude: nullableNumber,
    to_latitude: nullableNumber,
    to_longitude: nullableNumber,
  })
  .strict();

export const RiderDayTripLegRowsSchema = z.array(RiderDayTripLegRowSchema);

export const RiderDayTripJobRowSchema = z
  .object({
    rider_shift_id: requiredInteger,
    activity_type: z.string().min(1),
    work_id: requiredInteger,
    package_id: nullableInteger,
    return_id: nullableInteger,
    attempt_number: requiredInteger,
    attempt_outcome: nullableText,
    stop_number: nullableInteger,
    recorded_started_at: nullableText,
    effective_started_at: z.string().min(1),
    start_source: z.string().min(1),
    start_evidence_source: nullableText,
    started_by_rider: clickHouseFlag,
    recorded_ended_at: nullableText,
    effective_ended_at: z.string().min(1),
    end_source: z.string().min(1),
    terminal_evidence_source: nullableText,
    tap_to_address_meters: nullableNumber,
    visit_at: nullableText,
    visit_closest_meters: nullableNumber,
    stop_anchor_source: nullableText,
    stop_latitude: nullableNumber,
    stop_longitude: nullableNumber,
    address_latitude: nullableNumber,
    address_longitude: nullableNumber,
    tap_latitude: nullableNumber,
    tap_longitude: nullableNumber,
    is_start_inferred: clickHouseFlag,
    is_end_inferred: clickHouseFlag,
    is_address_mismatch: clickHouseFlag,
    is_open_unresolved: clickHouseFlag,
    is_unverified_failed_attempt: clickHouseFlag,
    ended_at_facility: clickHouseFlag,
    is_backend_only_start: clickHouseFlag,
    is_backend_only_completion: clickHouseFlag,
    is_late_mark_out: clickHouseFlag.optional().default(false),
    approach_estimate_km: nullableNumber.optional().default(null),
    is_completed_after_shift: clickHouseFlag.optional().default(false),
    is_carried_over: clickHouseFlag.optional().default(false),
    is_carried_over_unproven: clickHouseFlag.optional().default(false),
    /* Failed-attempt check (mesh mart_rider_failed_attempt_evidence), set only on
       failed deliveries / return pickups: did the rider go to the customer? */
    fail_check_verdict: nullableText.optional().default(null),
    fail_check_severity: nullableText.optional().default(null),
    fail_check_closest_meters: nullableNumber.optional().default(null),
    fail_check_dispatch_meters: nullableNumber.optional().default(null),
    fail_check_pct_way: nullableNumber.optional().default(null),
    fail_check_call_taps: nullableInteger.optional().default(null),
    fail_check_calls_connected: nullableInteger.optional().default(null),
    fail_check_failed_together: nullableInteger.optional().default(null),
    fail_check_quick: clickHouseFlag.optional().default(false),
    fail_check_pin_doubtful: clickHouseFlag.optional().default(false),
  })
  .strict();

export const RiderDayTripJobRowsSchema = z.array(RiderDayTripJobRowSchema);

export const RiderDayTripSegmentClassSchema = z.enum([
  'PAYABLE_WORK',
  'PAYABLE_RETURN',
  'AT_FACILITY',
  'PARKED',
  'NO_WORK_IN_HAND',
  'AFTER_LAST_WORK_NO_RETURN',
  'REJECTED_GPS',
]);

export const RiderDayTripSegmentRowSchema = z
  .object({
    rider_shift_id: requiredInteger,
    segment_number: requiredInteger,
    started_at: z.string().min(1),
    ended_at: z.string().min(1),
    gap_seconds: requiredNumber,
    from_latitude: requiredNumber,
    from_longitude: requiredNumber,
    to_latitude: requiredNumber,
    to_longitude: requiredNumber,
    segment_class: RiderDayTripSegmentClassSchema,
    measure_method: z.string().min(1),
    trip_number: nullableInteger,
    leg_number: nullableInteger,
    measured_meters: requiredNumber,
    estimated_meters: requiredNumber,
    payable_meters: requiredNumber,
    is_inferred_departure: clickHouseFlag,
  })
  .strict();

export const RiderDayTripSegmentRowsSchema = z.array(RiderDayTripSegmentRowSchema);

export type RiderDayTripSummaryRow = z.infer<typeof RiderDayTripSummaryRowSchema>;
export type RiderDayTripRow = z.infer<typeof RiderDayTripRowSchema>;
export type RiderDayTripLegRow = z.infer<typeof RiderDayTripLegRowSchema>;
export type RiderDayTripLegKind = z.infer<typeof RiderDayTripLegKindSchema>;
export type RiderDayTripJobRow = z.infer<typeof RiderDayTripJobRowSchema>;

/* Failed attempts per rider in the last 7 days (mesh card "Failed Attempts -
   Rider Last 7 Days"), read by the Rider Control Centre. Only riders with a
   High or Medium failed attempt. Leads, not proof. */
export const RiderFailedAttempts7dRowSchema = z
  .object({
    rider_id: requiredInteger,
    failed_7d: requiredInteger,
    high_7d: requiredInteger,
    medium_7d: requiredInteger,
    last_high_date: nullableText,
    last_failed_date: nullableText,
  })
  .strict();
export const RiderFailedAttempts7dRowsSchema = z.array(RiderFailedAttempts7dRowSchema);
export type RiderFailedAttempts7dRow = z.infer<typeof RiderFailedAttempts7dRowSchema>;
export type RiderDayTripSegmentRow = z.infer<typeof RiderDayTripSegmentRowSchema>;
export type RiderDayTripSegmentClass = z.infer<typeof RiderDayTripSegmentClassSchema>;

export type RiderDayTripBundle = {
  summary: RiderDayTripSummaryRow | null;
  trips: Array<RiderDayTripRow>;
  legs: Array<RiderDayTripLegRow>;
  jobs: Array<RiderDayTripJobRow>;
  segments: Array<RiderDayTripSegmentRow>;
};
